<template>
	<AppLayout :title="trans('settings.colleges.title')">
		<Modal id="create-college-modal">
			<template #title>{{ trans('settings.colleges.new.title') }}</template>
			<LabeledObject required>
				<template #label>{{ trans('settings.colleges.name') }}</template>
				<input v-model="newCollege.name" type="text">
			</LabeledObject>
			<LabeledObject required>
				<template #label>{{ trans('settings.colleges.acronym') }}</template>
				<input v-model="newCollege.acronym" type="text">
			</LabeledObject>
			<LabeledObject required>
				<template #label>{{ trans('settings.colleges.zone') }}</template>
				<select v-model="newCollege.college_zone">
					<option :value="null">{{ trans('settings.colleges.international') }}</option>
					<option v-for="collegeZone in CollegeZone" :value="collegeZone">{{ trans(`colleges.${collegeZone}`) }}</option>
				</select>
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-college-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Pagination :resource="colleges"/>
		<Empty v-if="colleges.data.length === 0"/>
		<CardGrid v-else>
			<Card v-for="college in colleges.data" :key="`colleges-${college.id}`">
				<LabeledObject required>
					<template #label>{{ trans('settings.colleges.name') }}</template>
					<input v-model="college.name" type="text">
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('settings.colleges.acronym') }}</template>
					<input v-model="college.acronym" type="text">
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('settings.colleges.zone') }}</template>
					<select v-model="college.college_zone">
						<option :value="null">{{ trans('settings.colleges.international') }}</option>
						<option v-for="collegeZone in CollegeZone" :value="collegeZone">{{ trans(`colleges.${collegeZone}`) }}</option>
					</select>
				</LabeledObject>
				<template #footer>
					<SecondaryButton :color="Color.Green" @click.stop="save(college)">
						<i class="fa-solid fa-save mr-2"/>
						{{ trans('general.save') }}
					</SecondaryButton>
					<SoftButton v-if="college.programs_count === 0" :color="Color.Red" @click.stop="destroy(college)">
						<i class="fa-solid fa-trash mr-2"/>
						{{ trans('general.delete') }}
					</SoftButton>
				</template>
			</Card>
		</CardGrid>
		<Pagination :resource="colleges"/>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Pagination from '@/Components/UI/Pagination.vue'
import {type College} from '@/Types/Settings/College'
import {CollegeZone} from '@/Types/Enums/CollegeZone'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type PaginatedModel} from '@/Types/Pagination/PaginatedModel'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	colleges:PaginatedModel<College>,
}>()

const working:Ref<boolean> = ref(false)
const colleges:Ref<PaginatedModel<College>> = ref(props.colleges)
const newCollege:Ref<College> = ref({
	name: '',
	acronym: '',
	college_zone: null,
} as College)

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('settings.colleges.store'), newCollege.value).then(({data}:{data:College}) => {
			colleges.value.data.push(data)
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function save(college:College):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('settings.colleges.update', college.id), college).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function destroy(college:College):void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('settings.colleges.delete', college.id)).then(() => {
			const index = colleges.value.data.findIndex((c:College) => c.id === college.id)
			if(index > -1){
				colleges.value.data.splice(index, 1)
			}
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>